import { FC, useMemo, useState } from "react";
import {
  CancelCaseType,
  VACANCY_START_AVAILABILITY,
} from "../../../../models/vacancy";
import { useAppSelector as useSelector, useAppDispatch as useDispatch } from "../../../../store";
import { cancelVacancyThunk } from "../../../../store/vacancy/thunks";
import { TVacancyDetail } from "../../../../store/vacancy/types";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Warning from "../../../UI/Icons/Warning";
import CharacterLimitTextarea from "../../../UI/Inputs/CharacterLimitTextarea";
import VacancyPosition from "../../../UI/VacancyPosition";
import { formatTimeframes } from "../../../Vacancy/timeframeApiHelper";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { useLocation, useNavigate } from "react-router";
import Roles from "../../../../models/roles";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import { isFulfilled } from "@reduxjs/toolkit";

interface ICancelVacancyProps {
  vacancy: TVacancyDetail;
  onClose: () => void;
}

const getCancelCaseType = (vacancy: TVacancyDetail): CancelCaseType => {
  const isAccepted = vacancy.status === "filled";

  if (!isAccepted) return CancelCaseType.SIMPLE;
  if (vacancy.timeline_type === VACANCY_START_AVAILABILITY.WITHIN_24_HOURS)
    return CancelCaseType.ACCEPTED_24H_LESS;
  return CancelCaseType.ACCEPTED_24H_PLUS;
};

const CancelVacancy: FC<ICancelVacancyProps> = ({ vacancy, onClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user)

  const caseType = getCancelCaseType(vacancy);
  const [comment, setComment] = useState("");

  const isCommentRequired =
    caseType === CancelCaseType.ACCEPTED_24H_PLUS ||
    caseType === CancelCaseType.ACCEPTED_24H_LESS;

  const isUrgent = caseType === CancelCaseType.ACCEPTED_24H_LESS;

  const isConfirmDisabled = isCommentRequired && comment.trim().length < 5;

  const formattedTimeframes = formatTimeframes(vacancy.timeframes);

  const isAdmin = useMemo(() => 
    user?.role.name === Roles.SUPER_ADMIN || user?.role.name === Roles.GENERAL_ADMIN
  , [user]);

  const renderInfoText = () => {
    switch (caseType) {
      case CancelCaseType.SIMPLE:
        return (
          <>
            <div className="fs-13 fw-400 text-black">
              You are about to cancel your vacancy:{" "}
              <VacancyPosition
                positions={vacancy?.vacancy_positions}
                alternatePosition={vacancy?.alternate_position_legacy || ""}
              />{" "}
              at{" "}
              {`${vacancy?.centre?.business_name}, ${vacancy?.centre?.address}`}{" "}
              on{" "}
              {formattedTimeframes.map((ftf, i) => (
                <div
                  key={i}
                  className={`fs-13 d-inline ${
                    ftf.alert ? "text-alert fw-700" : "fw-400"
                  }`}
                >
                  {ftf.label}
                </div>
              ))}
            </div>
          </>
        );
      case CancelCaseType.ACCEPTED_24H_PLUS:
      case CancelCaseType.ACCEPTED_24H_LESS:
        return (
          <>
            <div className="fs-13 fw-400 text-black">
              You are about to cancel your accepted vacancy:{" "}
              <VacancyPosition
                positions={vacancy?.vacancy_positions}
                alternatePosition={vacancy?.alternate_position_legacy || ""}
              />{" "}
              at{" "}
              {`${vacancy?.centre?.business_name}, ${vacancy?.centre?.address}`}{" "}
              on{" "}
              {formattedTimeframes.map((ftf, i) => (
                <span
                  key={i}
                  className={`fs-13 d-inline ${
                    ftf.alert ? "text-alert fw-700" : "fw-400"
                  }`}
                >
                  {ftf.label}
                  {i < formattedTimeframes.length - 1 && <span> and </span>}
                </span>
              ))}
            </div>
            <div className="fs-13 fw-400 text-black">
              Please include a reason for this cancellation and the candidate
              will be notified.
            </div>
          </>
        );
    }
  };

  const handleSubmit = async () => {
    try {
      dispatch(showTransparentPreloaderAction());
      const res = await dispatch(
        cancelVacancyThunk({
          active_vacancy_type:
            vacancy.status === "filled" ? "accepted" : "pending",
          uuid: vacancy.uuid,
          reason: caseType === CancelCaseType.SIMPLE ? null : comment.trim(),
        })
      ).unwrap(); // to catch errors in try/catch
      dispatch(hideTransparentPreloaderAction());
      if(res.status === "cancelled") {
        dispatch(showNotificationAction("Completed"));
        onClose(); // close the modal
        if(location.pathname.includes('vacancy-details')) {
          isAdmin ? navigate(AUTH_ROUTE.ADMIN_URGENT_VACANCIES) : navigate(AUTH_ROUTE.CLIENT_VACANCIES)
        }
      }
    } catch (err: any) {
      console.error("Cancel vacancy failed:", err);
      dispatch(hideTransparentPreloaderAction());
    }
  };

  return (
    <div className="d-flex flex-column gap-20">
      <div className="d-flex flex-column gap-15">
        <div className="fs-22 fw-400 text-black pr-15px">
          {caseType === CancelCaseType.SIMPLE
            ? "Cancel Vacancy"
            : "Cancel Vacancy and Notify Candidate"}
        </div>
        <div className="d-flex flex-column gap-8">{renderInfoText()}</div>
        {isCommentRequired && (
          <CharacterLimitTextarea
            maxLength={200}
            value={comment}
            onChange={(value) => setComment(value)}
          />
        )}
      </div>
      {isUrgent && (
        <div className="bg-teal-light radius-8px py-10px px-15px fs-13 fw-400 text-black d-flex gap-6 lh-20px">
          <Warning />
          Your cancellation of this urgent vacancy will incur a fee. The penalty
          fee of 4 hours is payable at quoted rate to the candidate who
          accepted.
        </div>
      )}

      <div className="d-flex justify-content-end">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          disabled={isConfirmDisabled}
          onClick={handleSubmit}
        >
          Confirm and Cancel
        </CustomButton>
      </div>
    </div>
  );
};

export default CancelVacancy;
